import { Fragment, forwardRef, useRef, type ButtonHTMLAttributes, type ReactElement, type ReactNode } from "react";
import { ContextMenu as Context, DropdownMenu as Dropdown, Tooltip as Tip } from "radix-ui";
import { Icon, type IconName } from "../Icon";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary" | "ghost" | "danger";
  size?: "small" | "normal" | "large";
  icon?: IconName;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button({ children, variant = "primary", size = "normal", icon, className = "", type = "button", ...props }, ref) {
  return <button {...props} ref={ref} type={type} className={`btn pa-btn ${variant} ${size} ${className}`}><span className="pa-button-content">{icon && <span className="pa-button-indicator"><Icon name={icon} mono={variant === "primary" || variant === "danger"} size={20} /></span>}<span className="pa-button-label">{children}</span></span></button>;
});

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  name: IconName;
  label: string;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton({ name, label, className = "", type = "button", ...props }, ref) {
  return <button {...props} ref={ref} type={type} className={`pa-icon-button ${className}`} aria-label={label}><Icon name={name} /></button>;
});

export function Tooltip({ children, content, side = "top" }: { children: ReactElement; content: ReactNode; side?: "top" | "right" | "bottom" | "left" }) {
  return <Tip.Provider delayDuration={350}><Tip.Root><Tip.Trigger asChild>{children}</Tip.Trigger><Tip.Portal><Tip.Content className="pa-tooltip" side={side} sideOffset={9}>{content}<Tip.Arrow className="pa-tooltip-arrow" /></Tip.Content></Tip.Portal></Tip.Root></Tip.Provider>;
}

export interface MenuItem {
  id: string;
  label: ReactNode;
  onSelect?: () => void;
  disabled?: boolean;
  danger?: boolean;
  checked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  separator?: boolean;
  icon?: IconName;
}

interface MenuProps {
  trigger: ReactElement;
  items: readonly MenuItem[];
  label?: string;
  align?: "start" | "center" | "end";
}

export function DropdownMenu({ trigger, items, label, align = "start" }: MenuProps) {
  return <Dropdown.Root><Dropdown.Trigger asChild>{trigger}</Dropdown.Trigger><Dropdown.Portal><Dropdown.Content className="pa-popup pa-menu-content" sideOffset={8} align={align} aria-label={label}>
    {label && <Dropdown.Label className="pa-menu-label">{label}</Dropdown.Label>}
    {items.map(item => <MenuEntry key={item.id} item={item} />)}
  </Dropdown.Content></Dropdown.Portal></Dropdown.Root>;
}

function MenuEntry({ item }: { item: MenuItem }) {
  const content = <>{item.icon && <Icon name={item.icon} size={20} />}<span>{item.label}</span></>;
  return <>{item.separator && <Dropdown.Separator className="pa-menu-separator" />}{item.checked !== undefined
    ? <Dropdown.CheckboxItem className="pa-menu-item" data-danger={item.danger || undefined} checked={item.checked} onCheckedChange={item.onCheckedChange} disabled={item.disabled} onSelect={item.onSelect}>{content}<Dropdown.ItemIndicator><Icon name="yes" size={18} /></Dropdown.ItemIndicator></Dropdown.CheckboxItem>
    : <Dropdown.Item className="pa-menu-item" data-danger={item.danger || undefined} disabled={item.disabled} onSelect={item.onSelect}>{content}</Dropdown.Item>}</>;
}

export function ContextMenu({ trigger, items, label }: MenuProps) {
  const triggerRef = useRef<HTMLElement>(null);
  return <Context.Root><Context.Trigger asChild ref={triggerRef} onKeyDown={event => {
    if (event.key === "ContextMenu" || event.key === "F10" && event.shiftKey) {
      event.preventDefault();
      const bounds = event.currentTarget.getBoundingClientRect();
      event.currentTarget.dispatchEvent(new MouseEvent("contextmenu", { bubbles: true, cancelable: true, clientX: bounds.left, clientY: bounds.bottom }));
    }
  }}>{trigger}</Context.Trigger><Context.Portal><Context.Content className="pa-popup pa-menu-content" aria-label={label} onCloseAutoFocus={event => { event.preventDefault(); triggerRef.current?.focus(); }}>
    {label && <Context.Label className="pa-menu-label">{label}</Context.Label>}
    {items.map(item => <Fragment key={item.id}>{item.separator && <Context.Separator className="pa-menu-separator" />}{item.checked !== undefined
      ? <Context.CheckboxItem className="pa-menu-item" data-danger={item.danger || undefined} checked={item.checked} onCheckedChange={item.onCheckedChange} disabled={item.disabled} onSelect={item.onSelect}>{item.label}<Context.ItemIndicator><Icon name="yes" size={18} /></Context.ItemIndicator></Context.CheckboxItem>
      : <Context.Item className="pa-menu-item" data-danger={item.danger || undefined} disabled={item.disabled} onSelect={item.onSelect}>{item.icon && <Icon name={item.icon} size={20} />}{item.label}</Context.Item>}</Fragment>)}
  </Context.Content></Context.Portal></Context.Root>;
}

export function SplitButton({ children, onClick, items, disabled, label = "更多操作" }: { children: ReactNode; onClick: ButtonProps["onClick"]; items: readonly MenuItem[]; disabled?: boolean; label?: string }) {
  return <div className="pa-split-button"><Button onClick={onClick} disabled={disabled}>{children}</Button><DropdownMenu trigger={<IconButton name="down" label={label} disabled={disabled} />} items={items} /></div>;
}
